import React, { useState, useEffect } from 'react';
import {
  Modal,
  Table,
  Button,
  Space,
  Select,
  Input,
  DatePicker,
  Typography,
  App,
  Alert,
  InputNumber,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import {
  useEligibleSalesOrdersForDirect,
  useCreateDirectDeliveryFromSalesOrder,
} from '@/hooks/useDelivery';
import { useActiveCustomers } from '@/hooks/useCustomers';
import warehouseService from '@/services/warehouse.service';
import type { DirectDeliveryEligibleSalesOrder, DirectDeliveryEligibleLine } from '@/services/delivery.service';

const { TextArea } = Input;

interface CreateDirectDeliveryFromSalesOrderModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (deliveryId: string, deliveryNumber: string) => void;
  /** Prefill customer filter and auto-select this SO when present in the eligible list */
  initialSalesOrderId?: string | null;
  initialCustomerId?: string | null;
}

export const CreateDirectDeliveryFromSalesOrderModal: React.FC<CreateDirectDeliveryFromSalesOrderModalProps> = ({
  open,
  onClose,
  onSuccess,
  initialSalesOrderId,
  initialCustomerId,
}) => {
  const { message } = App.useApp();
  const [step, setStep] = useState(0);
  const [customerIdFilter, setCustomerIdFilter] = useState<string | undefined>();
  const [selectedSo, setSelectedSo] = useState<DirectDeliveryEligibleSalesOrder | null>(null);
  const [lineQuantities, setLineQuantities] = useState<Record<string, number>>({});
  const [deliveryDate, setDeliveryDate] = useState(dayjs());
  const [customerPo, setCustomerPo] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [notes, setNotes] = useState('');
  const [warehouseId, setWarehouseId] = useState<string | undefined>();
  const [autoSelectAttempted, setAutoSelectAttempted] = useState(false);

  const { data: customers = [] } = useActiveCustomers();
  const { data: eligibleList = [], isLoading: loadingList } = useEligibleSalesOrdersForDirect(customerIdFilter);
  const createMutation = useCreateDirectDeliveryFromSalesOrder();
  const { data: warehouses = [] } = useQuery({
    queryKey: ['warehouses', 'dn-create'],
    queryFn: () => warehouseService.getAll(),
    enabled: open,
  });

  useEffect(() => {
    if (!open) {
      setStep(0);
      setSelectedSo(null);
      setLineQuantities({});
      setDeliveryDate(dayjs());
      setCustomerPo('');
      setDeliveryAddress('');
      setNotes('');
      setWarehouseId(undefined);
      setCustomerIdFilter(undefined);
      setAutoSelectAttempted(false);
      return;
    }
    if (initialCustomerId) {
      setCustomerIdFilter(initialCustomerId);
    }
  }, [open, initialCustomerId]);

  useEffect(() => {
    if (!open || warehouseId || warehouses.length === 0) return;
    const store =
      warehouses.find((w) => String(w.warehouse_type || '').toLowerCase() === 'store' && w.is_active !== false) ||
      warehouses.find((w) => w.is_active !== false) ||
      warehouses[0];
    if (store?.id) setWarehouseId(store.id);
  }, [open, warehouses, warehouseId]);

  useEffect(() => {
    if (!open || !initialSalesOrderId || autoSelectAttempted || loadingList) return;
    const match = eligibleList.find((row) => row.id === initialSalesOrderId);
    setAutoSelectAttempted(true);
    if (match) {
      setSelectedSo(match);
      setStep(1);
    }
  }, [open, initialSalesOrderId, eligibleList, loadingList, autoSelectAttempted]);

  useEffect(() => {
    if (selectedSo && step === 1) {
      const initial: Record<string, number> = {};
      selectedSo.line_items.forEach((li) => {
        initial[li.sales_order_item_id] = li.pending;
      });
      setLineQuantities(initial);
    }
  }, [selectedSo, step]);

  const handleSelectSo = (row: DirectDeliveryEligibleSalesOrder) => {
    setSelectedSo(row);
    setStep(1);
  };

  const handleBack = () => {
    setSelectedSo(null);
    setStep(0);
  };

  const handleSubmit = async () => {
    if (!selectedSo) return;
    if (!warehouseId) {
      message.warning('Select a source warehouse for stock issue');
      return;
    }
    const items = selectedSo.line_items
      .map((li) => ({
        sales_order_item_id: li.sales_order_item_id,
        delivered_quantity: lineQuantities[li.sales_order_item_id] ?? 0,
      }))
      .filter((i) => i.delivered_quantity > 0);
    if (items.length === 0) {
      message.warning('Enter at least one line with quantity greater than zero');
      return;
    }
    try {
      const delivery = await createMutation.mutateAsync({
        sales_order_id: selectedSo.id,
        delivery_date: deliveryDate.format('YYYY-MM-DD'),
        customer_po_number: customerPo || undefined,
        delivery_address: deliveryAddress || undefined,
        notes: notes || undefined,
        warehouse_id: warehouseId,
        items,
      });
      onSuccess?.(delivery.id, delivery.delivery_note_number);
      onClose();
    } catch {
      // mutation shows error
    }
  };

  const soColumns: ColumnsType<DirectDeliveryEligibleSalesOrder> = [
    { title: 'Customer order', dataIndex: 'sales_order_number', key: 'so', width: 140 },
    { title: 'Customer', dataIndex: 'customer_name', key: 'cust', ellipsis: true },
    {
      title: 'Lines',
      key: 'lines',
      width: 80,
      render: (_, row) => row.line_items.length,
    },
    {
      title: '',
      key: 'act',
      width: 100,
      render: (_, row) => (
        <Button type="primary" size="small" onClick={() => handleSelectSo(row)}>
          Select
        </Button>
      ),
    },
  ];

  const lineColumns: ColumnsType<DirectDeliveryEligibleLine> = [
    { title: '#', dataIndex: 'line_number', width: 50 },
    { title: 'Code', dataIndex: 'item_code', width: 100, render: (v) => v || '—' },
    { title: 'Description', dataIndex: 'item_description', ellipsis: true },
    { title: 'Pending', dataIndex: 'pending', width: 90 },
    {
      title: 'Deliver now',
      key: 'qty',
      width: 120,
      render: (_, li) => (
        <InputNumber
          min={0}
          max={li.pending}
          value={lineQuantities[li.sales_order_item_id] ?? 0}
          onChange={(v) =>
            setLineQuantities((prev) => ({
              ...prev,
              [li.sales_order_item_id]: typeof v === 'number' ? v : 0,
            }))
          }
          size="small"
        />
      ),
    },
    { title: 'UOM', dataIndex: 'uom', width: 70, render: (v) => v || '—' },
  ];

  return (
    <Modal
      title={
        step === 0
          ? 'Direct delivery from customer order'
          : `Direct delivery — ${selectedSo?.sales_order_number ?? ''}`
      }
      open={open}
      onCancel={onClose}
      width={720}
      footer={
        step === 0 ? (
          <Button onClick={onClose}>Close</Button>
        ) : (
          <Space>
            <Button onClick={handleBack}>Back</Button>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" loading={createMutation.isPending} onClick={handleSubmit}>
              Create delivery note
            </Button>
          </Space>
        )
      }
    >
      {step === 0 && (
        <>
          {initialSalesOrderId && autoSelectAttempted && !loadingList && !eligibleList.some((r) => r.id === initialSalesOrderId) && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="This customer order is not eligible for direct delivery"
              description="Confirm it is approved, has remaining quantity to deliver, and its revenue service type has Work order flow turned off (Sales → Revenue Service Types). Supply/manpower and trading/rental types should normally have Work order flow off; manufacturing types keep it on."
            />
          )}
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Orders listed here use a revenue service type with Work order flow off. For stock/consumable items, free quantity at the selected warehouse must cover the delivery quantity."
          />
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <div>
              <Typography.Text type="secondary">Filter by customer (optional)</Typography.Text>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="All customers"
                style={{ width: '100%', marginTop: 8 }}
                value={customerIdFilter}
                onChange={(v) => setCustomerIdFilter(v)}
                options={customers.map((c) => ({
                  value: c.id,
                  label: c.customer_name,
                }))}
              />
            </div>
            <Table
              rowKey="id"
              size="small"
              loading={loadingList}
              columns={soColumns}
              dataSource={eligibleList}
              pagination={{ pageSize: 8 }}
              locale={{ emptyText: 'No customer orders eligible for direct delivery' }}
            />
          </Space>
        </>
      )}

      {step === 1 && selectedSo && (
        <>
          <Space wrap style={{ marginBottom: 16 }}>
            <div>
              <Typography.Text type="secondary">Delivery date</Typography.Text>
              <DatePicker
                value={deliveryDate}
                onChange={(d) => d && setDeliveryDate(d)}
                style={{ display: 'block', marginTop: 4 }}
              />
            </div>
            <div>
              <Typography.Text type="secondary">Source warehouse</Typography.Text>
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Select warehouse"
                style={{ width: 260, marginTop: 4, display: 'block' }}
                value={warehouseId}
                onChange={(v) => setWarehouseId(v)}
                options={warehouses
                  .filter((w) => w.is_active !== false)
                  .map((w) => ({
                    value: w.id,
                    label: `${w.warehouse_code} — ${w.warehouse_name}`,
                  }))}
              />
            </div>
            <div>
              <Typography.Text type="secondary">Customer PO (optional)</Typography.Text>
              <Input
                value={customerPo}
                onChange={(e) => setCustomerPo(e.target.value)}
                style={{ width: 200, marginTop: 4 }}
              />
            </div>
          </Space>
          <div style={{ marginBottom: 12 }}>
            <Typography.Text type="secondary">Delivery address (optional)</Typography.Text>
            <TextArea rows={2} value={deliveryAddress} onChange={(e) => setDeliveryAddress(e.target.value)} style={{ marginTop: 4 }} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <Typography.Text type="secondary">Notes (optional)</Typography.Text>
            <TextArea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} style={{ marginTop: 4 }} />
          </div>
          <Table
            rowKey="sales_order_item_id"
            size="small"
            columns={lineColumns}
            dataSource={selectedSo.line_items}
            pagination={false}
          />
        </>
      )}
    </Modal>
  );
};
